import React, { useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { formatDate, formatTime } from '@/lib/utils';
import { retryOnCollision } from '@/components/schedule/collisionWarning';

const weekday = (iso) => {
  const jsDay = new Date(`${iso}T12:00:00`).getDay();
  return (jsDay + 6) % 7;
};

const row = (label, value) => (
  <div className="grid grid-cols-[5rem_1fr] gap-2 text-xs">
    <span className="text-slate-500">{label}</span>
    <span>{value || '—'}</span>
  </div>
);

const summary = (value) => (
  <>
    {row('Datum', formatDate(value.date))}
    {row('Uhrzeit', `${formatTime(value.start)}–${formatTime(value.end)}`)}
    {row('Raum', value.room_name)}
    {row('Lehrkraft', value.teacher_name)}
  </>
);

export default function RelocationConfirmDialog({ move, onClose, onSaved }) {
  const [mode, setMode] = useState('once');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (move) setMode('once');
  }, [move]);

  if (!move) return null;
  const { event, target } = move;
  const duration = Math.max(
    1,
    (Number(target.end?.slice(0, 2)) * 60 + Number(target.end?.slice(3, 5)))
      - (Number(target.start?.slice(0, 2)) * 60 + Number(target.start?.slice(3, 5))),
  );

  const confirm = async () => {
    setSaving(true);
    try {
      const postWithCollisionWarning = (path, payload) => retryOnCollision(
        () => api.post(path, payload),
        () => api.post(path, { ...payload, allow_collisions: true }),
      );
      if (mode === 'once') {
        if (!event.lesson_id) throw new Error('Für diesen Termin fehlt eine Termin-ID.');
        const saved = await postWithCollisionWarning(`/lessons/${event.lesson_id}/relocate`, {
          date: target.date,
          start_time: target.start,
          end_time: target.end,
          room_id: target.room_id || null,
          teacher_id: target.teacher_id || event.teacher_id || null,
        });
        if (!saved) return;
      } else {
        const saved = await postWithCollisionWarning(`/courses/${event.course_id}/periods`, {
          valid_from: target.date,
          day_of_week: weekday(target.date),
          start_time: target.start,
          duration_minutes: duration,
          room_id: target.room_id || null,
          teacher_id: target.teacher_id || event.teacher_id || null,
        });
        if (!saved) return;
      }
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error.message || 'Termin konnte nicht verschoben werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl" hideCloseButton data-testid="calendar-relocation-dialog">
        <DialogTitle>Termin verschieben</DialogTitle>
        <div className="grid grid-cols-2 gap-4">
          <section className="rounded border p-3">
            <h3 className="mb-2 text-xs font-bold uppercase text-slate-500">Bisher</h3>
            {summary(event)}
          </section>
          <section className="rounded border border-brand-red/30 bg-brand-red/[0.03] p-3">
            <h3 className="mb-2 text-xs font-bold uppercase text-slate-500">Neu</h3>
            {summary(target)}
          </section>
        </div>
        <fieldset className="space-y-2 text-sm">
          <legend className="mb-1 font-semibold">Gültigkeit</legend>
          <label className="flex items-center gap-2">
            <input type="radio" name="relocation-mode" checked={mode === 'once'} onChange={() => setMode('once')} />
            Nur diesen Termin verschieben
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="relocation-mode" checked={mode === 'permanent'} onChange={() => setMode('permanent')} />
            Ab diesem Datum dauerhaft ändern
          </label>
        </fieldset>
        <div className="flex justify-end gap-2">
          <button type="button" className="h-8 rounded border px-3 text-xs" onClick={onClose}>Abbrechen</button>
          <button
            type="button"
            className="h-8 rounded bg-emerald-600 px-3 text-xs font-semibold text-white disabled:opacity-50"
            disabled={saving}
            onClick={confirm}
          >
            {saving ? 'Speichert…' : 'Bestätigen'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
